import { gql, useMutation, useQuery } from '@apollo/client';
import luxon, { DateTime } from 'luxon';
import React, { useEffect, useRef } from 'react';
import { OrderForKivra } from '~Components/Storage';
import { useInterval, useSelector } from '~Hooks';
import { useLogger } from '~Hooks';
import { AdyenPaymentFromBackend, AdyenPaymentFromBackendVariables } from './__queries__';
import { KivraSendReceipt, KivraSendReceiptVariables } from './__queries__/KivraSendReceipt';
import { KivraAddressInput } from '../../../__queries__';

const BACKEND_ADYEN_PAYMENT = gql`
  query AdyenPaymentFromBackend($pspRef: String!, $venueId: ID!) {
    AdyenPayment(id: $pspRef, venueId: $venueId) {
      id
      method
      methodIdentifier
      createdAt
      pspReference
      tip
      token
      acquirerCode
      authorisationMid
      terminalId
      authCode
      issuerBin
    }
  }
`;

const KIVRA_SEND_RECEIPT = gql`
  mutation KivraSendReceipt($receipt: KivraSendReceiptInput!) {
    kivraSendReceipt(receipt: $receipt) {
      success
      transactionId
      kivraStatus
      error
    }
  }
`;

const VENUE_QUERY = gql`
  query KivraVenue($venueId: ID!) {
    venue: Venue(id: $venueId) {
      id
      name
      organizationId
      phone
      zip
      location {
        street
        city
        zip
      }
      company {
        id
        name
      }
    }
  }
`;

interface Props {
  data: OrderForKivra;
  onExpired: (id: string) => void;
}

const KivraInstance: React.FC<Props> = ({ data, onExpired }) => {
  const venueId = useSelector((state) => state.venueId);
  const logger = useLogger('kivra-sync');
  const hasSynced = useRef(false);

  // Replace 1UcR001772201432000.VWD6TBMGPMHP6V75 with VWD6TBMGPMHP6V75
  // But keep VWD6TBMGPMHP6V75 if its only VWD6TBMGPMHP6V75
  const pspRef = data.payment.meta.transactionId?.split('.').slice(-1)[0] || '';

  useEffect(() => {
    logger.info('Starting Kivra sync', {
      order: data.order,
      transactionId: data.payment.meta.transactionId,
      pspRef,
    });
  }, []);

  const backendQuery = useQuery<AdyenPaymentFromBackend, AdyenPaymentFromBackendVariables>(
    BACKEND_ADYEN_PAYMENT,
    {
      variables: {
        pspRef,
        venueId: venueId!,
      },
      pollInterval: 5000,
    },
  );
  const backendPayment = backendQuery.data?.AdyenPayment;

  const venueQuery = useQuery(VENUE_QUERY, {
    variables: { venueId },
    skip: !venueId,
  });
  const venue = venueQuery.data?.venue;

  const [sendReceipt] = useMutation<KivraSendReceipt, KivraSendReceiptVariables>(
    KIVRA_SEND_RECEIPT,
  );

  useEffect(() => {
    if (!venue || hasSynced.current || !backendPayment) {
      return;
    }

    // Prevent double-sync
    hasSynced.current = true;
    backendQuery.stopPolling();

    const cardToken = backendPayment?.token;
    if (!cardToken) {
      logger.warn('No card token available from Adyen payment, skipping Kivra sync', {
        backendPayment,
      });

      onExpired(data.order.id);
      return;
    }

    // Map cart items to Kivra item input
    const items = data.order.items.map((item) => ({
      id: item.itemId,
      name: item.title,
      fraction: item.fraction,
      amount: item.amount,
      discount: item.discount,
      vatRate: item.vatRate,
    }));

    // Build payment with card token and transaction fields
    const payments = [
      {
        amount: data.amount,
        type: data.payment.method,
        cardToken,
        acquiringInstitutionIdCode: backendPayment?.acquirerCode || undefined,
        cardAcceptorName: venue.name || undefined,
        cardAcceptorId: backendPayment?.authorisationMid || undefined,
        cardAcceptorTerminalId: backendPayment?.terminalId || undefined,
        approvalCode: backendPayment?.authCode || undefined,
        bankIdentificationNumber: backendPayment?.issuerBin || undefined,
      },
    ];

    // Calculate totals from items
    const totalAmount = data.order.items.reduce((sum, item) => sum + item.amount, 0);
    const totalDiscount = data.order.items.reduce((sum, item) => sum + item.discount, 0);

    const streetAddress = venue.location?.street;
    const locality = venue.location?.city;
    const postalCode = venue.zip || (venue.location?.zip ? String(venue.location.zip) : null);

    if (!streetAddress || !locality || !postalCode) {
      logger.warn(
        'Venue address is missing locality or postal code, Kivra receipt will have no address',
        {
          venueId,
          streetAddress,
          locality,
          postalCode,
        },
      );
    }

    const companyName = venue.company?.name || venue.name;

    const input: KivraSendReceiptVariables = {
      receipt: {
        venueId: venueId!,
        venueName: venue.name,
        companyName,
        orderId: data.order.id,
        createdAt:
          typeof data.order.createdAt === 'string'
            ? data.order.createdAt
            : data.order.createdAt.toISOString(),
        organizationNumber: venue.organizationId || '',
        phone: venue.phone || undefined,
        items,
        payments,
        address: {
          locality,
          postalCode,
          streetAddress,
        },
        amount: totalAmount,
        discount: totalDiscount,
        amountAfterDiscounts: totalAmount - totalDiscount,
      },
    };

    logger.info('Syncing order to Kivra', {
      input,
    });

    sendReceipt({
      variables: input,
    })
      .then((result) => {
        const kivraResult = result.data?.kivraSendReceipt;
        if (kivraResult?.success) {
          logger.info('Kivra receipt sent successfully', {
            orderId: data.order.id,
            transactionId: kivraResult.transactionId,
            kivraStatus: kivraResult.kivraStatus,
          });

          onExpired(data.order.id);
        } else {
          logger.error('Kivra receipt send failed', {
            orderId: data.order.id,
            errorMsg: kivraResult?.error,
          });
        }
      })
      .catch((err) => {
        onExpired(data.order.id);
        logger.error('Kivra receipt mutation error', {
          orderId: data.order.id,
          errorMsg: err.message,
        });
      });
  }, [backendPayment, venue]);

  useInterval(() => {
    const iso =
      typeof data.order.createdAt === 'string'
        ? data.order.createdAt
        : data.order.createdAt.toISOString();

    const timeSinceCreated = DateTime.fromISO(iso).diffNow('minutes');

    if (timeSinceCreated.minutes < 4) {
      backendQuery.stopPolling();
      onExpired(data.order.id);
    }
  }, 10000);

  return null;
};

export default KivraInstance;
